import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { launchTypesApi } from "../../api/endpoints";
import { ApiError } from "../../api/client";
import type { LaunchType } from "../../types/domain";
import { IcoPlus, IcoSparkle, IcoTrash } from "../../components/icons";
import { DynamicIcon } from "../../components/DynamicIcon";
import { CanWrite } from "../../components/CanWrite";
import { useCanWrite } from "../../hooks/useCanWrite";
import { DEFAULT_COLORS } from "../../lib/launchTypeMeta";
import { DEFAULT_LAUNCH_TYPE_ICON } from "../../lib/launchTypeIcons";

function pluralDays(n: number): string {
  if (n % 10 === 1 && n % 100 !== 11) return "день";
  if (n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20)) return "дня";
  return "дней";
}

function pluralLaunches(n: number): string {
  if (n % 10 === 1 && n % 100 !== 11) return "запуске";
  if (n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20)) return "запусках";
  return "запусках";
}

export function LaunchTypesPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const canWrite = useCanWrite();

  const query = useQuery({
    queryKey: ["launchTypes"],
    queryFn: async () => (await launchTypesApi.list()).launchTypes,
  });
  const items = query.data ?? [];

  const invalidate = () => qc.invalidateQueries({ queryKey: ["launchTypes"] });

  const createMutation = useMutation({
    mutationFn: () =>
      launchTypesApi.create({
        name: "Новый тип запуска",
        description: null,
        hint: null,
        icon: DEFAULT_LAUNCH_TYPE_ICON,
        color: DEFAULT_COLORS[0]!,
        typeSet: "full",
      }),
    onSuccess: ({ launchType }) => {
      invalidate();
      navigate(`/launch-types/${launchType.id}/edit`);
    },
  });

  const removeMutation = useMutation({
    mutationFn: ({ id, force }: { id: string; force?: boolean }) =>
      launchTypesApi.remove(id, force ? { force: true } : undefined),
    onSuccess: invalidate,
    onError: (err, vars) => {
      if (err instanceof ApiError) {
        if (err.code === "builtin_immutable") {
          window.alert("Встроенный тип запуска удалить нельзя.");
          return;
        }
        if (err.code === "type_in_use") {
          const d = err.details as { usageCount?: number } | undefined;
          const n = d?.usageCount ?? 0;
          const yes = window.confirm(
            `Тип используется в ${n} ${pluralLaunches(n)}. Они потеряют связь с этим типом. Всё равно удалить?`,
          );
          if (yes) {
            removeMutation.mutate({ id: vars.id, force: true });
          }
          return;
        }
      }
      if (err instanceof Error) window.alert(err.message);
    },
  });

  const sorted = useMemo(() => {
    return [...items].sort((a, b) => {
      if (a.isBuiltin !== b.isBuiltin) return a.isBuiltin ? -1 : 1;
      return a.sortOrder - b.sortOrder;
    });
  }, [items]);

  return (
    <div className="launches-page launch-types-page">
      <div className="launches-head">
        <div>
          <h1 className="launches-title">Типы запусков</h1>
          <div className="launches-subtitle">
            Шаблоны-заготовки: дни и карточки, которые разложатся при создании
            нового запуска. <b>{items.length}</b>{" "}
            {items.length === 1 ? "тип" : items.length >= 2 && items.length <= 4 ? "типа" : "типов"}.
            Клик по карточке — открыть редактор.
          </div>
        </div>
        <div className="launches-head-right">
          <CanWrite>
            <button
              type="button"
              className="btn-primary"
              onClick={() => createMutation.mutate()}
              disabled={createMutation.isPending}
            >
              <IcoPlus size={14} stroke="white" sw={2.5} />{" "}
              {createMutation.isPending ? "Создаём…" : "Новый тип"}
            </button>
          </CanWrite>
        </div>
      </div>

      {query.isLoading ? (
        <div className="launches-empty">
          <p>Загружаем…</p>
        </div>
      ) : query.isError ? (
        <div className="launches-empty">
          <h3>Не удалось загрузить типы запусков</h3>
          <p>{(query.error as Error).message}</p>
        </div>
      ) : (
        <div className="launch-types-grid">
          {sorted.map((t) => (
            <LaunchTypeCard
              key={t.id}
              item={t}
              canWrite={canWrite}
              onOpen={() => navigate(`/launch-types/${t.id}/edit`)}
              onDelete={() => {
                if (window.confirm(`Удалить тип «${t.name}»?`)) {
                  removeMutation.mutate({ id: t.id });
                }
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function LaunchTypeCard({
  item,
  canWrite,
  onOpen,
  onDelete,
}: {
  item: LaunchType;
  canWrite: boolean;
  onOpen: () => void;
  onDelete: () => void;
}) {
  const byStage = item.daysByStage ?? { warmup: 0, sales: 0, close: 0 };
  const total = byStage.warmup + byStage.sales + byStage.close;
  const cardCount = item._count?.cards ?? 0;
  const configured = total > 0;
  return (
    <button type="button" className="launch-type-card" onClick={onOpen}>
      <div className="launch-type-card-head">
        <div
          className="launch-type-icon"
          style={{ background: `${item.color}20`, color: item.color }}
        >
          <DynamicIcon name={item.icon} size={22} color={item.color} />
        </div>
        <div className="launch-type-card-meta">
          <b className="launch-type-name">{item.name}</b>
          <span className="launch-type-desc">
            {item.description ?? "Без описания"}
          </span>
        </div>
        {item.isBuiltin && (
          <span className="launch-type-badge" title="Встроенный — редактируется, но не удаляется">
            <IcoSparkle size={10} stroke="currentColor" />
            Встроенный
          </span>
        )}
      </div>
      <div className="launch-type-card-stats">
        <Stat label="Прогрев" value={byStage.warmup} />
        <Stat label="Эфиры" value={byStage.sales} />
        <Stat label="Дожим" value={byStage.close} />
        <Stat label="Всего" value={total} highlight />
      </div>
      <div className="launch-type-card-foot">
        <span className="launch-type-typeset">
          {configured ? (
            <>
              Шаблон: <b>{total}</b> {pluralDays(total)}, <b>{cardCount}</b>{" "}
              {cardCount === 1 ? "карточка" : cardCount >= 2 && cardCount <= 4 ? "карточки" : "карточек"}
            </>
          ) : (
            <span style={{ color: "var(--text-3)" }}>Шаблон пуст — кликните, чтобы настроить</span>
          )}
        </span>
        {canWrite && !item.isBuiltin && (
          <button
            type="button"
            className="row-del"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            title="Удалить"
          >
            <IcoTrash size={13} />
          </button>
        )}
      </div>
    </button>
  );
}

function Stat({
  label,
  value,
  highlight,
}: {
  label: string;
  value: number;
  highlight?: boolean;
}) {
  return (
    <div className={"launch-type-stat" + (highlight ? " highlight" : "")}>
      <span>{label}</span>
      <b>
        {value} {pluralDays(value)}
      </b>
    </div>
  );
}
